import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/**
 * Stats odometer — each figure rolls up to its value when scrolled into view.
 *
 * Adapted to this site's markup rather than the reference's data-odometer-*
 * attributes: the group is [data-stats] (the block) and each rolling number is
 * .block__stats__stat, so nothing had to change in the template.
 *
 * Non-digits in a stat ("92%", "£1.2m", "3x") are kept as static characters and
 * only the digits roll, so units and separators stay put.
 *
 * The DOM is rebuilt as a column of digits per place, translated on the Y axis;
 * after the animation it is flattened back to plain text so the final markup is
 * no different from the server-rendered version.
 *
 * No-ops when the markup isn't present, and skipped entirely under
 * prefers-reduced-motion (the numbers just render at their final value).
 */

const DEFAULTS = {
  duration: 2,          // Seconds for a digit to reach its value
  ease: "power3.out",
  elementStagger: 0.1,  // Between stats in the same block
  digitStagger: 0.04,   // Between digits within one stat
  triggerStart: "top 80%",
  digitCycles: 2,       // Full 0-9 spins before settling
};

function initStats() {
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  document.querySelectorAll("[data-stats]").forEach((group) => {
    // Guard against double-init if called twice on the same DOM.
    if (group.hasAttribute("data-stats-initialized")) return;
    group.setAttribute("data-stats-initialized", "");

    const elements = Array.from(group.querySelectorAll(".block__stats__stat"));
    if (!elements.length) return;

    // Reduced motion: skip the roll, but still split each stat into per-
    // character spans so the suffix styling (see blocks/stats.css) applies.
    if (prefersReducedMotion) {
      elements.forEach((el) => {
        const text = el.textContent.trim();
        if (!/\d/.test(text)) return;
        const step = lineHeightRatio(el);
        el.innerHTML = "";
        parseSegments(text).forEach((seg) => {
          const span = document.createElement("span");
          span.setAttribute(
            "data-odometer-part",
            seg.type === "digit" ? "mask" : "static",
          );
          // Same inline sizing the animated path applies, so both routes lay
          // out identically.
          span.style.height = step + "em";
          span.style.lineHeight = step;
          span.textContent = seg.char;
          el.appendChild(span);
        });
      });
      return;
    }

    const elementData = elements
      .map((el) => {
        const originalText = el.textContent.trim();
        // Nothing to roll if the stat has no digits (e.g. "N/A").
        if (!/\d/.test(originalText)) return null;

        const step = lineHeightRatio(el);
        const segments = parseSegments(originalText);
        const rollers = buildRollerDOM(el, segments, step);

        return { el, rollers, step, originalText };
      })
      .filter(Boolean);

    if (!elementData.length) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: group,
        start: DEFAULTS.triggerStart,
        once: true,
      },
      onComplete() {
        // Flatten back to plain text so the resting DOM is clean.
        elementData.forEach(({ el, originalText }) =>
          cleanupElement(el, originalText),
        );
      },
    });

    elementData.forEach(({ rollers, step }, orderIdx) => {
      const offset = orderIdx * DEFAULTS.elementStagger;

      rollers.forEach(({ roller, targetPos }, digitIdx) => {
        // Reversed so the LAST digit settles first, which reads as the number
        // counting up rather than unspooling left to right.
        const reversedIdx = rollers.length - 1 - digitIdx;
        tl.to(
          roller,
          {
            y: -targetPos * step + "em",
            duration: DEFAULTS.duration,
            ease: DEFAULTS.ease,
            force3D: true,
          },
          offset + reversedIdx * DEFAULTS.digitStagger,
        );
      });
    });
  });
}

/* Helpers ---------------------------------------------------- */

// The roller moves in multiples of the line height, so it needs that as a
// ratio of the font size to stay correct at any size.
function lineHeightRatio(el) {
  const cs = getComputedStyle(el);
  if (cs.lineHeight === "normal") return 1.2;
  return parseFloat(cs.lineHeight) / parseFloat(cs.fontSize);
}

// Split "92%" into rolling digits and static characters.
function parseSegments(text) {
  return [...text].map((char) => ({
    type: /\d/.test(char) ? "digit" : "static",
    char,
  }));
}

// Replace the element's text with a span per character: statics render as-is,
// digits become a masked column of 0-9 that scrolls to the target.
function buildRollerDOM(el, segments, step) {
  el.innerHTML = "";
  const rollers = [];
  const totalCells = 10 * DEFAULTS.digitCycles;

  segments.forEach((seg) => {
    if (seg.type === "static") {
      const span = document.createElement("span");
      span.setAttribute("data-odometer-part", "static");
      span.style.height = step + "em";
      span.style.lineHeight = step;
      span.textContent = seg.char;
      el.appendChild(span);
      return;
    }

    const mask = document.createElement("span");
    mask.setAttribute("data-odometer-part", "mask");
    mask.style.height = step + "em";
    mask.style.lineHeight = step;

    const roller = document.createElement("span");
    roller.setAttribute("data-odometer-part", "roller");
    roller.style.lineHeight = step;

    // 0-9 repeated, so the digit can spin past a full cycle before landing.
    const digits = [];
    for (let d = 0; d < totalCells; d++) digits.push(d % 10);
    roller.textContent = digits.join("\n");

    mask.appendChild(roller);
    el.appendChild(mask);

    gsap.set(roller, { y: 0 });

    // Always start from 0 and travel a full cycle plus the target, so every
    // digit visibly spins rather than nudging one place.
    const endDigit = parseInt(seg.char, 10);
    rollers.push({ roller, targetPos: 10 + endDigit });
  });

  return rollers;
}

// Strip the roller machinery but KEEP the per-character spans, so the static
// segments (%, £, x…) stay individually styleable — see the suffix rule in
// blocks/stats.css. Rebuilding as plain text would drop them.
function cleanupElement(el, originalText) {
  const digits = [...originalText].filter((c) => /\d/.test(c));
  let di = 0;

  el.querySelectorAll('[data-odometer-part="mask"]').forEach((mask) => {
    // ONLY swap the roller column for the final digit. The inline height and
    // overflow are deliberately left in place: clearing them changed each mask
    // from a fixed-height clipped box to an auto-height one, which shifted the
    // layout the instant the animation finished — the "snap" on completion.
    mask.textContent = digits[di++] || "";
  });
}

export { initStats };
